import {defineStore} from "pinia";
import {ref} from "vue";
import i18n from '@/plugins/i18n';
import { PROCEDURE_ROUND } from '@/lib/procedure';

const DRAFT = 'draft';
const PUBLISHED = 'published';
const REVIEW = 'review';
const SUMMING = 'summing';
const AGREEMENT = 'agreement';
const CANCELED = 'canceled';
const COMPLETED = 'completed';

export const useProcedureStepStore = defineStore('procedureStep', () => {
    const steps = ref([
        {
            name: 'Черновик',
            code: DRAFT,
            order: 100,
            roles: ['ROLE_CUSTOMER', 'ROLE_ADMIN'],
            round: PROCEDURE_ROUND.Basic,
        },
        {
            name: 'Приём заявок',
            code: PUBLISHED,
            order: 200,
            roles: ['ROLE_SUPPLIER', 'ROLE_CUSTOMER', 'ROLE_ADMIN'],
            round: PROCEDURE_ROUND.Basic,
        },
        {
            name: 'Рассмотрение заявок',
            code: REVIEW,
            order: 300,
            roles: ['ROLE_SUPPLIER', 'ROLE_CUSTOMER', 'ROLE_ADMIN'],
            round: PROCEDURE_ROUND.Basic,
        },
        {
            name: 'Подведение итогов',
            code: SUMMING,
            order: 320,
            roles: ['ROLE_SUPPLIER', 'ROLE_CUSTOMER', 'ROLE_ADMIN'],
            round: PROCEDURE_ROUND.Basic,
        },
        {
            name: 'Завершена',
            code: COMPLETED,
            order: 700,
            roles: ['ROLE_SUPPLIER', 'ROLE_CUSTOMER', 'ROLE_ADMIN'],
            round: PROCEDURE_ROUND.Basic,
        },
        {
            name: 'Отменена',
            code: CANCELED,
            order: 900,
            roles: ['ROLE_SUPPLIER', 'ROLE_CUSTOMER', 'ROLE_ADMIN'],
            round: PROCEDURE_ROUND.Basic,
        }
    ]);

    function addStep(step) {
        if (undefined === step.round) {
            step.round = PROCEDURE_ROUND.Basic
        }
        const exists = steps.value.some(s =>
          s.code === step.code
          && s.order === step.order
          && s.round === step.round
        );

        if (!exists) {
            steps.value.push(step);
        }
    }

    function getStepByCodeAndRound(code, round = PROCEDURE_ROUND.Basic) {
        return steps.value.find(item => item.code === code && item.round === round);
    }

    function getProcedureSteps(user) {
        return steps.value.filter(step =>
            step.roles.some(role => user.hasRole(role)) || (!user.hasOpts() && step.code !== DRAFT)
        ).sort(
            (a, b) => a.order - b.order
        );
    }

    const COLORS = ref({
        draft: 'var(--step-draft-color)',
        published: 'var(--step-published-color)',
        review: 'var(--step-review-color)',
        summing: 'var(--step-summing-color)',
        agreement: 'var(--step-agreement-color)',
        completed: 'var(--step-completed-color)',
        canceled: 'var(--step-canceled-color)',
    });

    function addStatusColor(name, color) {
        COLORS.value[name] = color;
    }

    /**
     * Шаги процедуры, у которых отображается крайний срок (label "до:").
     */
    const DEADLINES = {
        published: 'applicationDeadline',
        review: 'reviewDeadline',
        summing: 'summingStart',
    };

    /**
     * Шаги процедуры, у которых отображается дата начала (label "с:").
     */
    const STARTS = {};

    /**
     * Добавляет шаг с дедлайном (используется модулем).
     * @param key
     * @param name
     */
    function addDeadlines(key, name) {
        DEADLINES[key] = name;
    }

    /**
     * Добавляет шаг с датой начала (используется модулем).
     * @param key
     * @param name
     */
    function addStarts(key, name) {
        STARTS[key] = name;
    }

    /**
     * Возвращает мета-информацию о дате, связанную с шагом процедуры.
     * Включает имя поля и метку ("до:" или "с:")
     * @param statusCode
     */
    function getDeadlineMeta(statusCode) {
        if (STARTS[statusCode]) {
            return { field: STARTS[statusCode], label: i18n.global.t('с:') };
        }
        if (DEADLINES[statusCode]) {
            return { field: DEADLINES[statusCode], label: i18n.global.t('до:') };
        }
        return null;
    }

    const SUBMITTED_VIEW_KEY_STATUS_FIELDS = {
        published: null,
        review: null,
        summing: 'isAccepted',
        agreement: 'isWinner',
        completed: 'isWinner'
    }

    function addSubmittedViewKey(key, value) {
        SUBMITTED_VIEW_KEY_STATUS_FIELDS[key] = value;
    }

    function getSubmittedKeyByStatus(stepCode) {
        return SUBMITTED_VIEW_KEY_STATUS_FIELDS[stepCode] ?? SUBMITTED_VIEW_KEY_STATUS_FIELDS[PUBLISHED];
    }

    function getStatusColor(stepCode) {
        return COLORS.value[stepCode] ?? COLORS.value[DRAFT];
    }

    function getStatusStyle(stepCode) {
        return `background-color: ${getStatusColor(stepCode)} !important;`;
    }

    function getDeadlineField(stepCode) {
        return DEADLINES[stepCode];
    }

    return {
        DRAFT,
        PUBLISHED,
        REVIEW,
        SUMMING,
        AGREEMENT,
        CANCELED,
        COMPLETED,
        COLORS,
        addStarts,
        addDeadlines,
        getDeadlineMeta,
        addStatusColor,
        getStatusColor,
        getStatusStyle,
        addStep,
        getProcedureSteps,
        getDeadlineField,
        getSubmittedKeyByStatus,
        addSubmittedViewKey,
        getStepByCodeAndRound,
    }
})
